import { useMemo } from "react";
import type { AxiosRequestConfig } from "axios";
import axios, { AxiosError } from "axios";
import type {
  Options,
  RefetchFunction,
  ResponseValues,
  UseAxiosResult,
} from "axios-hooks";
import { makeUseAxios } from "axios-hooks";
import { debounce } from "lodash-es";

import { NotificationEventSchema } from "@thalamos/vision-common";

import { config } from "../../config/index.js";
import { useChannel } from "../../lib/pusher/core/useChannel.js";
import { useEvent } from "../../lib/pusher/core/useEvent.js";
import { redirectToLogin } from "./redirectToLogin.js";
import { checkVersionMismatch } from "./versionCheck.js";

export const apiAxios = axios.create({
  // Important for cookies etc.
  withCredentials: true,
  baseURL: config.backendUrl,
  validateStatus: (status) => status >= 200 && status < 500,
});

// Because `validateStatus` above admits any status < 500, a 4xx error body
// resolves onto `response.data` rather than throwing an AxiosError. Callers that
// branch on `.status` therefore need `data` typed as one of two shapes. Spell the
// pair out at each call site via `ResponseBody<Success, Failure>` — not behind a
// single alias — so type hints show both arms. It is a plain union, not a
// discriminated neverthrow Result: the arms are distinguished by HTTP status,
// which axios cannot bind to `data` in the type system.
export type ResponseBody<Success, Failure> = Success | Failure;

// A serialised backend error, as it arrives on `response.data` on the failure
// path: `message` from a BaseError, `reason` from an explicit TsoaResponse, and
// `message` + `details` from a TSOA `ValidateError` (a 400 validation failure).
// `details` is TSOA's field-errors map, which no frontend caller reads, so it is
// left as `unknown` rather than pulling in the TSOA type.
export type ErrorBody = {
  message?: string;
  reason?: string;
  details?: unknown;
};

// The success shape for endpoints that return 204 with no body. It is
// `Record<string, never>` (not `undefined`/`void`) so a diagnostic field reads
// off a `ResponseBody` union without narrowing; the empty object encodes "no
// content" and is not the wire value (a 204 body is the empty string).
export type NoContentBody = Record<string, never>;

apiAxios.interceptors.response.use((response) => {
  if (response.status === 401) {
    redirectToLogin();
  }

  return response;
});

apiAxios.interceptors.response.use((response) => {
  checkVersionMismatch(response.headers["x-code-version"]);
  return response;
});

const useRawApiAxios = makeUseAxios({ axios: apiAxios, cache: false });

/**
 * Takes the body of a resolved non-2xx answer, in practice a 4xx refusal, off
 * `data`, where axios-hooks puts it typed as the payload, and reports it on
 * `error` as it would a failure. `response` keeps it for a caller that acts on
 * a particular status.
 *
 * A 401 is not reported: the interceptor above is already sending the browser
 * to log in, and a page that showed its error state would only flash it on
 * the way out.
 */
function narrowRefusal<TResponse, TBody>(
  state: ResponseValues<TResponse, TBody, unknown>,
): ResponseValues<TResponse, TBody, unknown> {
  const { response } = state;
  if (
    response === undefined ||
    (response.status >= 200 && response.status < 300)
  ) {
    return state;
  }

  // axios-hooks clears `error` as a request starts, so a refusal must clear
  // then too.
  const reportRefusal = !state.loading && response.status !== 401;

  return {
    ...state,
    data: undefined,
    error:
      state.error ??
      (reportRefusal
        ? new AxiosError(
            `Request failed with status code ${String(response.status)}`,
            AxiosError.ERR_BAD_REQUEST,
            response.config,
            response.request,
            response,
          )
        : null),
  };
}

/** axios-hooks over `apiAxios`, with a refusal narrowed by `narrowRefusal`. */
export function useApiAxios<TResponse, TBody = unknown>(
  config: AxiosRequestConfig<TBody> | string,
  options?: Options,
): UseAxiosResult<TResponse, TBody, unknown> {
  const [state, execute, cancel] = useRawApiAxios<TResponse, TBody, unknown>(
    config,
    options,
  );

  // `state` changes only when a request starts or settles, and callers may
  // use its fields as effect dependencies; a refusal rebuilt on every render
  // would re-run them each time.
  const narrowedState = useMemo(() => narrowRefusal(state), [state]);

  return [narrowedState, execute, cancel];
}

/**
 * Whether a hook's `error` is the server refusing the request (a 4xx), as
 * opposed to failing to answer it.
 */
export function isRefusal(error: AxiosError | null): boolean {
  const status = error?.response?.status;
  return status !== undefined && status >= 400 && status < 500;
}

/*
  Creates a hook that automatically reloads useAxios data based on events received on a pusher channel
 */
export function usePusherChannelReload<TResponse, TRequestBody = undefined>(
  channelName: string,
  config: AxiosRequestConfig<TRequestBody> | string,
  options?: Options,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unnecessary-type-arguments
): UseAxiosResult<TResponse, any, any> {
  const channel = useChannel(channelName);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = useApiAxios<TResponse>(config as any, options);
  const reloadFn = debounce(() => {
    return result[1]();
  }, 500);
  useEvent(
    channel,
    NotificationEventSchema.enum.resourceReload,
    (_event: unknown) => {
      void reloadFn();
    },
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const debouncedReloadFn = reloadFn as RefetchFunction<any, TResponse>;
  return [result[0], debouncedReloadFn, result[2]];
}
